import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Paperclip, Trash2 } from 'lucide-react';
import { api } from '../../services/api';
import downloadBlob from '../../utils/downloadBlob';
import { formatDateTime } from '../format';

const KINDS = ['source', 'result'];

function formatSize(bytes) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileColumn({ kind, files, leadId, onChanged }) {
  const { t } = useTranslation('common');
  const picker = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(null);
  const [dragging, setDragging] = useState(false);

  const problem = (e) => e?.response?.data?.detail || e.message;

  // One file a request: a batch of scans would otherwise pass the proxy's size limit as one body.
  const upload = async (picked) => {
    if (picked.length === 0) return;
    setBusy(true);
    setError('');
    try {
      for (const file of picked) {
        // eslint-disable-next-line no-await-in-loop
        await api.uploadCrmLeadFiles(leadId, kind, [file]);
      }
    } catch (e) {
      setError(t('crm.files.uploadFailed', { error: problem(e) }));
    } finally {
      setBusy(false);
      await onChanged();
    }
  };

  const download = async (file) => {
    setError('');
    try {
      downloadBlob(await api.downloadCrmLeadFile(leadId, file.id), file.filename);
    } catch (e) {
      setError(t('crm.files.downloadFailed', { error: problem(e) }));
    }
  };

  const remove = async (file) => {
    setConfirming(null);
    setError('');
    try {
      await api.removeCrmLeadFile(leadId, file.id);
    } catch (e) {
      setError(t('crm.files.removeFailed', { error: problem(e) }));
    }
    await onChanged();
  };

  return (
    <div
      className={`crm-files-column${dragging ? ' dragging' : ''}`}
      data-kind={kind}
      onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        upload(Array.from(event.dataTransfer.files));
      }}
    >
      <div className="crm-files-head">
        <h3>{t(`crm.files.${kind}`)}</h3>
        <button type="button" className="crm-btn" disabled={busy} onClick={() => picker.current.click()}>
          <Paperclip size={15} />
          {busy ? t('crm.files.uploading') : t('crm.files.add')}
        </button>
        <input
          ref={picker}
          type="file"
          multiple
          hidden
          onChange={(event) => {
            const picked = Array.from(event.target.files);
            event.target.value = '';
            upload(picked);
          }}
        />
      </div>
      {files.length === 0 ? (
        <p className="crm-files-empty">{t(`crm.files.${kind}Empty`)}</p>
      ) : (
        <ul className="crm-files-list">
          {files.map((file) => (
            <li key={file.id}>
              <button type="button" className="crm-files-name" title={file.filename} onClick={() => download(file)}>
                <Download size={14} />
                <span>{file.filename}</span>
              </button>
              <span className="crm-files-meta">
                {formatSize(file.size_bytes)} · {file.uploaded_by_name} · {formatDateTime(file.uploaded_at)}
              </span>
              {confirming === file.id ? (
                <button type="button" className="crm-btn danger crm-files-confirm" onClick={() => remove(file)}>
                  {t('crm.files.confirmRemove')}
                </button>
              ) : (
                <button
                  type="button"
                  className="crm-icon-btn"
                  aria-label={t('crm.files.remove', { name: file.filename })}
                  title={t('crm.files.remove', { name: file.filename })}
                  onClick={() => setConfirming(file.id)}
                >
                  <Trash2 size={15} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {error && <p className="crm-error" role="alert">{error}</p>}
    </div>
  );
}

// What the client sent to be translated and what went back to them, kept on the order.
export default function OrderFiles({ lead, onChanged }) {
  const { t } = useTranslation('common');
  return (
    <section className="crm-card" data-area="files">
      <h2>{t('crm.files.title')}</h2>
      <p className="crm-files-note">{t('crm.files.note')}</p>
      <div className="crm-files">
        {KINDS.map((kind) => (
          <FileColumn
            key={kind}
            kind={kind}
            files={lead.files.filter((file) => file.kind === kind)}
            leadId={lead.id}
            onChanged={onChanged}
          />
        ))}
      </div>
    </section>
  );
}
